import { BaseComponent } from "./base-component.js"
import { customElement, property } from "lit/decorators.js"
import { html, css, TemplateResult, CSSResultGroup } from "lit"

export const HEADER_STYLES = {
  TEXT: 'TEXT',
  LOGO: 'LOGO',
  LOGO_AND_TEXT: 'LOGO_AND_TEXT'
}

/**
 * slots:
 *  - right-side
 */
@customElement("il-page-header")
export default class PageHeaderComponent extends BaseComponent {
  @property({ attribute: 'logo-url', type: String })
  logoUrl?: string = undefined

  @property({ attribute: 'logo-label', type: String })
  logoLabel?: string = undefined

  @property({ attribute: 'header-style', type: String})
  headerStyle: string = HEADER_STYLES.TEXT

  static override styles = [
    BaseComponent.styles,
    css`
      .il-page-header {
        --il-page-header-height: 56px;
        --il-page-header-padding-x: 1rem;
        --il-page-header-padding-y: .5rem;
        --il-page-header-logo-width: 150px;
        --il-page-header-logo-label-color: var(--il-color-neutrals-0);
        --il-page-header-logo-label-font-size: var(--il-font-size-16);
        --il-page-header-logo-label-font-weight: var(--il-font-weight-xbold);
        --il-page-header-logo-label-line-height: var(--il-font-line-height-150);
        --il-page-header-logo-label-letter-spacing: var(--il-font-letter-spacing-2);

        display: flex;
        justify-content: space-between;

        height: var(--il-page-header-height);
        width: 100%;
        min-width: 0; /* IMPORTANT to allow children to shrink */

        padding: var(--il-page-header-padding-y) var(--il-page-header-padding-x);

        background-image: var(--il-gradient-header);
      }

      .il-page-header.il-page-header--no-right-side .il-page-header__logo-label-container {
        max-width: 100%;
        overflow: hidden;
      }

      .il-page-header__left-container {
        display: flex;
        gap: 1rem;
        flex-shrink: 1;
        flex-grow: 0;

        width: 100%;
        min-width: 0;
      }

      .il-page-header__logo-container {
        max-width: var(--il-page-header-logo-width);
        height: 40px;

        object-fit: contain;
      }

      .il-page-header__logo-container img {
        height: 100%;
      }

      .il-page-header__logo-container + .il-page-header__logo-label-container {
        border-left: 1px solid var(--il-color-white-opacity-25);
        padding-left: 1rem;
      }

      .il-page-header__logo-label-container {
        display: flex;
        align-items: center;
        overflow: hidden;

        font-size: var(--il-page-header-logo-label-font-size);
        font-weight: var(--il-page-header-logo-label-font-weight);
        line-height: var(--il-page-header-logo-label-line-height);
        letter-spacing: var(--il-page-header-logo-label-letter-spacing);

        color: var(--il-page-header-logo-label-color);
      }

      .il-page-header__logo-label-container
        .il-page-header__logo-label-container__label {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .il-page-header__right-container {
        flex-shrink: 0;
        flex-grow: 0;
        color: var(--il-page-header-logo-label-color);
      }

      @media (min-width: 640px) {
        .il-page-header {
          --il-page-header-height: 64px;
          --il-page-header-padding-y: .75rem;
          --il-page-header-logo-label-font-size: var(--il-font-size-20);
          --il-page-header-logo-label-line-height: var(--il-font-line-height-125);
        }
      }
    `
  ] as CSSResultGroup

  override render (): TemplateResult {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-page-header
        "
        part="page-header"
        data-test-iliad-page-header
      >
        <div class="il-page-header__left-container" part="page-header-left-container">
          ${this._renderLogoHtmL()}
          ${this._renderLogoLabel()}
        </div>

        <div class="il-page-header__right-container" part="page-header-right-container">
          <slot name="right-side"></slot>
        </div>
      </div>
    `
  }

  _renderLogoHtmL (): TemplateResult {
    const enabledHeaderStyles = [HEADER_STYLES.LOGO, HEADER_STYLES.LOGO_AND_TEXT]

    if (this.logoUrl && (enabledHeaderStyles.includes(this.headerStyle))) {
      return html`
        <div
          class="il-page-header__logo-container"
          part="page-header-logo-container"
          data-test-iliad-page-header-logo-container
        >
          <img src="${this.logoUrl}" alt="company logo">
        </div>
      `
    }

    return html``
  }

  _renderLogoLabel (): TemplateResult {
    const enabledHeaderStyles = [HEADER_STYLES.TEXT, HEADER_STYLES.LOGO_AND_TEXT]

    if (this.logoLabel && (enabledHeaderStyles.includes(this.headerStyle))) {
      return html`
        <div
          class="il-page-header__logo-label-container ${
            this.headerStyle === HEADER_STYLES.TEXT
              ? 'il-page-header__logo-label-container--text-only'
              : ''
          }"
          part="page-header-logo-label-container"
          data-test-iliad-page-header-logo-label-container
        >
          <div class="il-page-header__logo-label-container__label">
            ${this.logoLabel}
          </div>
        </div>
      `
    }

    return html``
  }
}
